import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AppProvider, useApp } from "@/state/AppContext";
import { isAdminHost } from "@/lib/adminHost";
import { onUnauthorized } from "@/lib/api";
import { hereOf, loginPath } from "@/lib/loginPath";
import PortalShell from "@/layouts/PortalShell";
import PublicBill from "@/pages/PublicBill";
import CityHome from "@/pages/CityHome";
import Directory from "@/pages/Directory";
import WorkerDirectory from "@/pages/WorkerDirectory";
import WorkerProfile from "@/pages/WorkerProfile";
import BusinessMiniSite from "@/pages/BusinessMiniSite";
import Cart from "@/pages/Cart";
import Community from "@/pages/Community";
import CommunityPost from "@/pages/CommunityPost";
import MyActivity from "@/pages/MyActivity";
import Favourites from "@/pages/Favourites";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Services from "@/pages/Services";
import OnboardingRequest from "@/pages/OnboardingRequest";
import JoinWorker from "@/pages/JoinWorker";
import Landmark from "@/pages/Landmark";
import Offers from "@/pages/Offers";
import OfferDetail from "@/pages/OfferDetail";
import EventsList from "@/pages/EventsList";
import EventDetail from "@/pages/EventDetail";
import JobsList from "@/pages/JobsList";
import JobDetail from "@/pages/JobDetail";
import Pay from "@/pages/Pay";
import Ambulance from "@/pages/Ambulance";
import AmbulanceStatus from "@/pages/AmbulanceStatus";
import OrderDetail from "@/pages/OrderDetail";
import AppointmentDetail from "@/pages/AppointmentDetail";
import BookingDetail from "@/pages/BookingDetail";
import ReservationDetail from "@/pages/ReservationDetail";
import TaskDetail from "@/pages/TaskDetail";
import CheckinLanding from "@/pages/CheckinLanding";
import PlatformHome from "@/pages/PlatformHome";
import NoAccess from "@/pages/NoAccess";
import { AsLogo } from "@/components/AsLogo";
import { Button } from "@/components/ui/button";
import GoogleCallback from "@/pages/GoogleCallback";
import VerifyPhone from "@/pages/VerifyPhone";
import PolicyPage from "@/pages/PolicyPage";
import Pricing from "@/pages/Pricing";
import DeleteAccount from "@/pages/DeleteAccount";
import AcceptTerms from "@/pages/AcceptTerms";

// The three role consoles load as their own chunks. They are the only place `recharts` is used —
// five dashboard panels — and recharts plus its d3 and decimal.js dependencies are ~757 KB of the
// bundle's source. A citizen opening /<city> never sees a chart, and before this split paid for
// the whole charting stack, plus the operator and admin consoles, before the homepage could
// render. Each console is reached by an explicit navigation, so a brief fallback is acceptable
// where a blocked first paint is not.
const OwnerDashboard = lazy(() => import("@/pages/OwnerDashboard"));
const OperatorDashboard = lazy(() => import("@/pages/OperatorDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

// The admin site (apnashahar.online's `admin.` host / apnashahar-admin-<env>.web.app) is the SAME
// SPA bundle as the city app, baked once and published to two Firebase sites. When we are being
// served from the admin host (`isAdminHost`, shared with AppContext — lib/adminHost.js) we expose
// ONLY the platform console (AdminPortal). This is UX hardening ONLY: the security boundary is the
// server's AdminHostMiddleware (web/middleware.py), which refuses platform routes off this host
// and city/tenant routes on it. A spoofed hostname changes nothing the server enforces.

/** True when the signed-in person may reach a `platform_admin`-gated route. The authority is the
 * `is_platform_admin` flag — what the SERVER's `require_roles` reads (identity/service.py) — NOT
 * the active role: a platform admin who signs in with no city context (e.g. on the admin host,
 * whose Google flow carries no tenant) has no `platform_admin` role profile minted, so
 * `active_role_type` is `operator`/`null` while the flag is true. Checking only `active_role_type`
 * is what sent such an admin into a `/platform` -> `/account` redirect loop (the blank page). */
function roleAllowed(auth, roles) {
  if (!roles) return true;
  if (roles.includes(auth.active_role_type)) return true;
  return Boolean(auth.is_platform_admin) && roles.includes("platform_admin");
}

/** `citySensitive` routes (`/city-desk`, `/owner`) are staff consoles scoped to ONE city seat.
 * The backend scopes every read to the JWT's `tid`, never the URL (`web/deps.py`), so rendering
 * `/biswan/city-desk` on a Sitapur token would show Biswan's chrome over Sitapur's data (L4-002).
 *
 * R11 MULTICITY: a person can hold seats in several cities (`auth.seats`). On a city mismatch the
 * Guard looks for the caller's own seat, of a role this route allows, in the URL's city:
 *   - found → switch to it (`switchRole` re-binds the token to that city) and then render;
 *   - none  → a terminal 403 page (`CityNoAccess`), never a redirect to another city.
 * Platform admin is exempt — legitimately city-wide, not a single-tenant seat. */
export function Guard({ roles, citySensitive, children }) {
  const { auth, booting, tenant, tenants, switchRole, t } = useApp();
  const location = useLocation();
  const [switchFailed, setSwitchFailed] = useState(false);

  const ownSlug =
    citySensitive && auth && auth.active_role_type !== "platform_admin" && !auth.is_platform_admin
      ? tenants.find((c) => c.id === auth.active_tenant_id)?.slug
      : null;
  const cityMismatch = Boolean(ownSlug && tenant && ownSlug !== tenant.slug);
  const urlSeat = cityMismatch
    ? (auth.seats || []).find(
        (s) => s.tenant_slug === tenant.slug && (!roles || roles.includes(s.role_type)),
      )
    : null;
  const urlSeatId = urlSeat?.id || null;

  useEffect(() => {
    if (!cityMismatch || !urlSeatId) return undefined;
    let cancelled = false;
    // Same URL city as the seat, so `switchRole` only swaps the token and `auth` (no navigation);
    // the next render has no mismatch and shows the console.
    switchRole(urlSeatId).catch(() => {
      if (cancelled) return;
      setSwitchFailed(true);
      toast.error(t("operator.citySwitch.failed"));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityMismatch, urlSeatId]);

  if (booting) return null;
  if (!auth) return <Navigate to={loginPath(hereOf(location), tenant?.slug)} replace />;
  // `needs_phone` deliberately does NOT gate here: a citizen browses and uses their account without
  // a number, and is asked ONCE at the first action that needs reachability (the inline PhoneGate,
  // opened by the server's 403 `phone_not_verified` - see lib/phoneGate.js). `needs_terms` stays:
  // Guard only wraps account/desk routes, never plain browsing, and terms are a one-time sign-up step.
  if (auth.needs_terms) return <Navigate to="/accept-terms" replace />;
  if (!roleAllowed(auth, roles)) return <Navigate to="/account" replace />;
  if (cityMismatch) {
    if (urlSeatId && !switchFailed) return null; // switching
    return <CityNoAccess ownSlug={ownSlug} path={`${location.pathname}${location.search}`} />;
  }
  return children;
}

/** The 403 for a city desk the signed-in person has no seat in (R11 MULTICITY). Terminal, like
 * the admin host's `NoAccess`: it says why and offers the way to a city they DO work in. Going
 * there is a real navigation, because the router's `basename` is the city. */
export function CityNoAccess({ ownSlug, path }) {
  const { t, lang, auth, tenant, tenants } = useApp();
  const cityLabel = (c) => (c ? (lang === "hi" ? c.name_hi || c.name : c.name) : "");
  const own = tenants.find((c) => c.slug === ownSlug);
  const name = auth?.identity?.display_name || auth?.identity?.email || "";
  return (
    <div className="grid min-h-[60vh] place-items-center px-6" data-testid="city-no-access">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
        <AsLogo />
        <h1 className="text-lg font-semibold text-foreground">{t("noAccess.cityTitle")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("noAccess.cityBody", { name, city: cityLabel(tenant) })}
        </p>
        {own && (
          <Button
            type="button"
            variant="outline"
            data-testid="city-no-access-go"
            onClick={() => window.location.assign(`/${own.slug}${path || ""}`)}
          >
            {t("noAccess.goToCity", { city: cityLabel(own) })}
          </Button>
        )}
      </div>
    </div>
  );
}

// Only requires a signed-in session — NOT needs_phone/needs_terms. This wraps /verify-phone, which
// is *where* a needs_phone user completes their number, so redirecting to it here (as Guard does)
// self-loops /verify-phone -> /verify-phone and renders a blank page. VerifyPhone drives its own
// phone -> otp -> profile steps and navigates onward when done.
function RequireAuth({ children }) {
  const { auth, booting } = useApp();
  if (booting) return null;
  if (!auth) return <Navigate to="/login" replace />;
  return children;
}

/** The /platform gate for the ADMIN HOST. Unlike `Guard`, a signed-in person who is NOT a platform
 * admin gets a terminal `<NoAccess>` page, NOT a `<Navigate to="/account">` — that route does not
 * exist in `AdminPortal`, so the `*` funnel bounces it back to `/platform` and the guard fires
 * again: the blank-page redirect loop. Signed out → `/login` (which renders here); the only two
 * outcomes are the console, the sign-in page, or the No-access page — never a bounce. */
export function RequirePlatformAdmin({ children }) {
  const { auth, booting } = useApp();
  if (booting) return null;
  if (!auth) return <Navigate to="/login" replace />;
  if (!roleAllowed(auth, ["platform_admin"])) return <NoAccess />;
  return children;
}

function Loading() {
  const { t, bootSlow, bootError, retryBoot } = useApp();
  return (
    <div
      className="grid min-h-screen place-items-center text-muted-foreground"
      data-testid="app-loading"
    >
      {bootError ? (
        <div
          className="flex flex-col items-center gap-3 px-6 text-center"
          data-testid="app-boot-error"
        >
          <p>{t("boot.failed")}</p>
          <button
            type="button"
            className="rounded-full border border-border px-4 py-1.5 text-sm text-foreground"
            data-testid="app-boot-retry"
            onClick={retryBoot}
          >
            {t("boot.retry")}
          </button>
        </div>
      ) : bootSlow ? (
        <div
          className="flex flex-col items-center gap-3 px-6 text-center"
          data-testid="app-boot-slow"
        >
          <p>{t("boot.slow")}</p>
          <button
            type="button"
            className="rounded-full border border-border px-4 py-1.5 text-sm text-foreground"
            data-testid="app-boot-retry"
            onClick={retryBoot}
          >
            {t("boot.retry")}
          </button>
        </div>
      ) : (
        "…"
      )}
    </div>
  );
}

/** Shown while a lazily-loaded console chunk is in flight. Deliberately not the `Loading`
 * component above: that one renders boot failure and retry affordances, and a chunk fetch is not
 * a boot. Same centred treatment so the transition does not jump. */
function RouteFallback() {
  return (
    <div
      className="grid min-h-screen place-items-center text-muted-foreground"
      data-testid="route-loading"
    >
      …
    </div>
  );
}

/** Every city portal is mounted under /<city>, so all links inside it stay city-relative. */
function CityPortal({ basename }) {
  return (
    <BrowserRouter basename={basename}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<PortalShell />}>
            <Route path="/" element={<CityHome />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/workers" element={<WorkerDirectory />} />
            <Route path="/w/:id" element={<WorkerProfile />} />
            <Route path="/services" element={<Services />} />
            <Route path="/onboarding-request" element={<OnboardingRequest />} />
            {/* Workers are app-only (D1): this explains it and links to the app. */}
            <Route path="/join-worker" element={<JoinWorker />} />
            {/* Public, not Guard-wrapped — the QR poster/daily-code target; it never checks
                anyone in itself (§4.1), so it needs no sign-in gate. */}
            <Route path="/checkin/:businessId" element={<CheckinLanding />} />
            {/* Public, not Guard-wrapped — Call 108 must work signed out, same as mobile. */}
            <Route path="/ambulance" element={<Ambulance />} />
            <Route
              path="/ambulance/:id"
              element={
                <Guard>
                  <AmbulanceStatus />
                </Guard>
              }
            />
            <Route
              path="/order/:id"
              element={
                <Guard>
                  <OrderDetail />
                </Guard>
              }
            />
            <Route
              path="/appointments/:id"
              element={
                <Guard>
                  <AppointmentDetail />
                </Guard>
              }
            />
            <Route
              path="/bookings/:id"
              element={
                <Guard>
                  <BookingDetail />
                </Guard>
              }
            />
            <Route
              path="/reservations/:id"
              element={
                <Guard>
                  <ReservationDetail />
                </Guard>
              }
            />
            <Route
              path="/tasks/:id"
              element={
                <Guard>
                  <TaskDetail />
                </Guard>
              }
            />
            <Route path="/community" element={<Community />} />
            <Route path="/community/:postId" element={<CommunityPost />} />
            {/* Policy pages. All four are public and must stay reachable signed out: Google Play
              will not publish an app without a live privacy policy URL, and requires a web
              deletion route someone who has already uninstalled can still find. Their names are
              in config.RESERVED_SLUGS so no business can ever take them. */}
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<PolicyPage doc="privacy" />} />
            <Route path="/terms" element={<PolicyPage doc="terms" />} />
            <Route
              path="/emergency-disclaimer"
              element={<PolicyPage doc="emergency" />}
            />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/accept-terms" element={<AcceptTerms />} />
            <Route path="/login" element={<Login />} />
            {/* D-10: staff/admin sign-in (username/password + Google staff) lives on its own
              route, not on the public consumer /login page. Not linked from the consumer flow —
              admins/operators reach it by URL. */}
            <Route path="/staff-login" element={<Login initialStep="admin" />} />
            <Route path="/auth/google" element={<GoogleCallback />} />
            <Route
              path="/verify-phone"
              element={
                <RequireAuth>
                  <VerifyPhone />
                </RequireAuth>
              }
            />
            <Route
              path="/account"
              element={
                <Guard>
                  <Account />
                </Guard>
              }
            />
            <Route
              path="/cart"
              element={
                <Guard>
                  <Cart />
                </Guard>
              }
            />
            <Route
              path="/activity"
              element={
                <Guard>
                  <MyActivity />
                </Guard>
              }
            />
            <Route
              path="/favourites"
              element={
                <Guard>
                  <Favourites />
                </Guard>
              }
            />
            <Route
              path="/pay/:businessId"
              element={
                <Guard>
                  <Pay />
                </Guard>
              }
            />
            <Route
              path="/owner"
              element={
                <Guard roles={["business_owner"]} citySensitive>
                  <OwnerDashboard />
                </Guard>
              }
            />
            {/* Payment push notifications link to /owner/payments, which had no route at all and
              fell through to the catch-all redirect. Lands on the Collections tab, where the
              owner confirms the money arrived. */}
            <Route
              path="/owner/payments"
              element={
                <Guard roles={["business_owner"]}>
                  <Navigate to="/owner?view=collections" replace />
                </Guard>
              }
            />
            {/* "New order received" links to /owner/orders/<id> (the app opens that order on its
              Orders tab). The web console has no per-order page, so it lands on the Orders view. */}
            <Route
              path="/owner/orders/:id"
              element={
                <Guard roles={["business_owner"]}>
                  <Navigate to="/owner?view=orders" replace />
                </Guard>
              }
            />
            {/* Target of the ambulance provider push-notification link. The website has no
              per-booking owner deep link anywhere (every other owner notification link already
              just points at /owner generically), so this lands the provider on the dashboard,
              where the Ambulance tab shows the request. */}
            <Route
              path="/provider/ambulance/:id"
              element={
                <Guard roles={["business_owner"]}>
                  <Navigate to="/owner" replace />
                </Guard>
              }
            />
            <Route
              path="/city-desk"
              element={
                <Guard roles={["operator", "platform_admin"]} citySensitive>
                  <OperatorDashboard />
                </Guard>
              }
            />
            <Route
              path="/platform"
              element={
                <Guard roles={["platform_admin"]}>
                  <AdminDashboard />
                </Guard>
              }
            />
            <Route path="/place/:slug" element={<Landmark />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/offers/:id" element={<OfferDetail />} />
            <Route path="/events" element={<EventsList />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/jobs" element={<JobsList />} />
            <Route path="/jobs/:id" element={<JobDetail />} />
            {/* A shop sits directly on the city path; /b/<slug> stays as a permanent alias. */}
            <Route path="/b/:slug" element={<BusinessMiniSite />} />
            <Route path="/:slug" element={<BusinessMiniSite />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/** R3-4: the admin host mounts no `SignInPrompt`, so an expired session (any admin read answering
 * 401 "Your admin session has ended") used to leave the panel on its skeleton forever. Register
 * the same module-level 401 hook the citizen app uses, but here the answer is to drop the dead
 * token and go to the admin `/login`. Probes that opt out (`skipAuthPrompt`, e.g. the boot
 * `/auth/me`) never reach it, so reload / new-tab persistence is unaffected, and a wrong password
 * on /login itself is left to that form. */
export function AdminSessionExpiry() {
  const { signOut } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  useEffect(() => {
    const onLogin = path === "/login" || path === "/staff-login";
    onUnauthorized(() => {
      if (onLogin) return;
      signOut();
      navigate("/login", { replace: true });
    });
    return () => onUnauthorized(null);
  }, [path, signOut, navigate]);
  return null;
}

/** The admin host serves ONLY the platform console. Same bundle as the city app; here the router
 * exposes just `/platform` (plus the admin Google sign-in the console depends on — Guard sends an
 * unauthenticated admin to `/login`) and funnels every other path to `/platform`. UX hardening
 * only — the server's AdminHostMiddleware is the real boundary. */
function AdminPortal() {
  return (
    <BrowserRouter>
      <AdminSessionExpiry />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route
            path="/platform"
            element={
              <RequirePlatformAdmin>
                <AdminDashboard />
              </RequirePlatformAdmin>
            }
          />
          <Route path="/login" element={<Login initialStep="admin" />} />
          <Route path="/staff-login" element={<Login initialStep="admin" />} />
          <Route path="/auth/google" element={<GoogleCallback />} />
          <Route path="*" element={<Navigate to="/platform" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function Shell() {
  const { booting, citySlug, tenant } = useApp();
  // `/bill/:token` (P2-002, screen 6b) is a public, tenant-agnostic link — it carries no city
  // segment, so it is handled OUTSIDE city resolution entirely, before the `booting`/`citySlug`
  // gates below even apply. See `PublicBill.jsx`'s module docstring.
  if (window.location.pathname.startsWith("/bill/")) return <PublicBill />;
  if (booting) return <Loading />;
  // The admin host: the console and nothing else (its own router). Checked after boot so Guard
  // sees resolved auth. No city segment here, so this stands in for the PlatformHome branch below.
  if (isAdminHost()) return <AdminPortal />;
  if (!citySlug) return <PlatformHome />;
  if (!tenant) return <Loading />;
  return <CityPortal basename={`/${citySlug}`} />;
}

export default function App() {
  return (
    <AppProvider>
      {/* top-center, offset under the sticky header: top-right sat on the header's Sign-in button. */}
      <Toaster position="top-center" offset={72} richColors duration={4000} />
      <Shell />
    </AppProvider>
  );
}
